'use client'

import {
  Dialog,
  DialogPanel,
  Transition,
  TransitionChild,
} from '@headlessui/react'
import React, { Fragment, ReactNode } from 'react'
import clsx from 'clsx'
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion'
import {
  softEditorialEase,
  softMotionDuration,
} from '@/shared/components/motion/motion-tokens'

const MaxWidthMap = {
  lg: 'max-w-lg',
  xl: 'max-w-xl',
  '2xl': 'max-w-2xl',
  '3xl': 'max-w-3xl',
  '4xl': 'max-w-4xl',
  '5xl': 'max-w-5xl',
  '6xl': 'max-w-6xl',
  '7xl': 'max-w-7xl',
}

export type PanelWidth = keyof typeof MaxWidthMap

export type DialogModalProps = {
  show: boolean

  onClose: () => void
  fullWidth?: boolean
  children?: ReactNode
  panelClasses?: string
  panelContainerClasses?: string
  width?: PanelWidth
  motionPreset?: 'default' | 'editorial'
}

export function DialogModal({
  show,
  onClose,
  children,
  fullWidth = true,
  width = 'lg',
  panelClasses = '',
  panelContainerClasses = '',
  motionPreset = 'default',
}: DialogModalProps) {
  const editorialMotion = motionPreset === 'editorial'

  if (editorialMotion) {
    return (
      <EditorialDialogModal
        show={show}
        onClose={onClose}
        fullWidth={fullWidth}
        width={width}
        panelClasses={panelClasses}
        panelContainerClasses={panelContainerClasses}
      >
        {children}
      </EditorialDialogModal>
    )
  }

  return (
    <Transition show={show} as={Fragment}>
      <Dialog
        open={show}
        as="div"
        className="relative z-[100]"
        onClose={onClose}
      >
        <TransitionChild
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </TransitionChild>

        <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
          <div
            className={clsx(
              'flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0',
              panelContainerClasses,
            )}
          >
            <TransitionChild
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <DialogPanel
                className={clsx(
                  'relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl sm:my-8 sm:p-6',
                  'transition-all',
                  MaxWidthMap[width],
                  panelClasses,
                  fullWidth && 'w-full',
                )}
              >
                {children}
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </Dialog>
    </Transition>
  )
}

function EditorialDialogModal({
  show,
  onClose,
  children,
  fullWidth,
  width,
  panelClasses,
  panelContainerClasses,
}: Required<
  Pick<
    DialogModalProps,
    | 'show'
    | 'onClose'
    | 'fullWidth'
    | 'width'
    | 'panelClasses'
    | 'panelContainerClasses'
  >
> &
  Pick<DialogModalProps, 'children'>) {
  const prefersReducedMotion = useReducedMotion()

  return (
    <AnimatePresence>
      {show && (
        <Dialog
          key="editorial-dialog"
          open
          as="div"
          className="relative z-[100]"
          onClose={onClose}
        >
          <motion.div
            aria-hidden="true"
            className="fixed inset-0 bg-[#0B0B0F]/35 backdrop-blur-[2px]"
            initial={prefersReducedMotion ? false : { opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={
              prefersReducedMotion
                ? { duration: 0 }
                : {
                    duration: softMotionDuration.panel,
                    ease: softEditorialEase,
                  }
            }
          />

          <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
            <div
              className={clsx(
                'flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0',
                panelContainerClasses,
              )}
            >
              <motion.div
                className={clsx(
                  'flex justify-center',
                  MaxWidthMap[width],
                  fullWidth && 'w-full',
                )}
                initial={prefersReducedMotion ? false : { opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                exit={
                  prefersReducedMotion ? { opacity: 0 } : { opacity: 0, y: 8 }
                }
                transition={
                  prefersReducedMotion
                    ? { duration: 0 }
                    : {
                        duration: softMotionDuration.panel,
                        ease: softEditorialEase,
                      }
                }
              >
                <DialogPanel
                  className={clsx(
                    'relative w-full overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl sm:my-8 sm:p-6',
                    panelClasses,
                  )}
                >
                  {children}
                </DialogPanel>
              </motion.div>
            </div>
          </div>
        </Dialog>
      )}
    </AnimatePresence>
  )
}
